import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Lock, Mail } from "lucide-react";
import { AlertMessage } from "@/components/ui/alert-message";
import { AuthLayout } from "@/components/layout/AuthLayout";

export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showVerificationLink, setShowVerificationLink] = useState(false);
  const { signIn, signInWithGoogle, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for emailConfirmed query parameter
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const emailConfirmed = queryParams.get("emailConfirmed");

    if (emailConfirmed === "true") {
      setSuccessMessage("Email verified successfully! You can now sign in.");
      // Remove the query parameter from the URL without refreshing the page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location.search]);

  // Get the page user was trying to visit before being redirected to login.
  // A ?redirect= query param (e.g. from an invitation link) wins; only
  // same-origin absolute paths are honoured.
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const safeRedirect =
    redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
      ? redirectParam
      : null;
  const from = safeRedirect || location.state?.from?.pathname || "/";

  const handleGoogleSignIn = async () => {
    setError("");
    try {
      await signInWithGoogle();
      // Redirect to the page user was trying to visit or to dashboard
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(
        err.message || "Failed to sign in with Google. Please try again."
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setShowVerificationLink(false);

    if (!email || !password) {
      setError("Email and password are required");
      return;
    }

    try {
      // Store the current values before sign-in attempt
      const currentEmail = email;
      const currentPassword = password;

      await signIn(currentEmail, currentPassword);
      // Redirect to the page user was trying to visit or to dashboard
      navigate(from, { replace: true });
    } catch (err: any) {
      // Check if the error is related to email verification
      const errorMessage =
        err.message || "Failed to sign in. Please check your credentials.";

      if (
        errorMessage.toLowerCase().includes("email") &&
        (errorMessage.toLowerCase().includes("verify") ||
          errorMessage.toLowerCase().includes("verification") ||
          errorMessage.toLowerCase().includes("unverified"))
      ) {
        setShowVerificationLink(true);
      }

      // On error, keep the form values and show the error message
      setError(errorMessage);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to access your dashboard"
      leftContent={{
        title: "Smart Insights",
        subtitle: "& Analytics",
        description:
          "Monitor your AI-powered visual commerce tools with comprehensive analytics. Track performance, user engagement, and conversion metrics across all your features in one unified dashboard.",
        trustIndicator: "Real-time data insights",
      }}
    >
      <AlertMessage message={error} type="error" />
      <AlertMessage message={successMessage} type="success" />

      {showVerificationLink && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-700 text-sm mb-2">
            Need to verify your email address?
          </p>
          <Link
            to="/verification-pending"
            className="text-sm font-medium text-blue-600 hover:text-blue-500 underline"
          >
            Go to verification page to resend email →
          </Link>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="email" className="sr-only">
            Email address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              id="email"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              className="pl-10 h-12 border-gray-200 focus:border-purple-500 focus:ring-purple-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="sr-only">
            Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              id="password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              className="pl-10 h-12 border-gray-200 focus:border-purple-500 focus:ring-purple-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-end">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-purple-600 hover:text-purple-500"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          className="w-full h-12 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-medium rounded-lg transition-colors"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Signing in...
            </>
          ) : (
            "Sign in"
          )}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">
              OR CONTINUE WITH
            </span>
          </div>
        </div>

        <Button
          type="button"
          onClick={handleGoogleSignIn}
          className="w-full h-12 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors"
          disabled={isLoading}
        >
          <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </Button>
      </form>

      <div className="mt-6 text-center space-y-2">
        <p className="text-sm text-gray-600">
          Don't have an account?{" "}
          <Link
            to="/signup"
            className="font-medium text-purple-600 hover:text-purple-500"
          >
            Sign up
          </Link>
        </p>
        <p className="text-xs text-gray-500">
          Need to verify your email?{" "}
          <Link
            to="/resend-verification"
            className="font-medium text-purple-600 hover:text-purple-500"
          >
            Resend verification email
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
